import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showUndoToast } from "../../../../../helper/undoToast";
import { useDispatch, useSelector } from "react-redux";
import {
  useDeleteNoteMutation,
  useUpdateNoteMutation,
} from "../../../../../store/apiSlice/newInterfaceApiSlice";
import { dispatchSetNotes, resolveNoteOrderType } from "../../../../../store/apiSlice/noteCache";
import {
  operationsApiSlice,
  useCreateOperationsNoteMutation,
} from "../../../../../store/apiSlice/operationsApiSlice";
import { useGetTransferOrderNotesQuery } from "../../../../../store/apiSlice/orderApiSlice";
import { useGetPurchaseOrderNotesQuery } from "../../../../../store/apiSlice/purchaseOrderApiSlice";
import {
  useCreateConsignmentNoteMutation,
  useGetConsignmentNotesQuery,
} from "../../../../../store/apiSlice/shipmentApiSlice";
import {
  orderTypeForUrl,
  selectStateForNotes,
  selectStateForOrder,
} from "../../utils/tableDataElements";
import {
  ActivityFooter,
  EmptyState,
  Filters,
  Layout,
  LedgerHeader,
  LedgerTitle,
  ScrollRegion,
} from "./NotesComposer.styles";
import { NotesList } from "./NotesList";
import { ShimmerUiForNotes } from "./ShimmerUiForNotes";
import { TaskComposer } from "./TaskComposer";

/**
 * Counts open tasks from an array of notes without allocating intermediate arrays.
 *
 * @param {Array<Object>} notes List of notes/tasks.
 * @return {number} Count of uncompleted tasks.
 */
export function countOpenTasks(notes) {
  if (!Array.isArray(notes)) return 0;
  let count = 0;
  for (let i = 0; i < notes.length; i += 1) {
    const item = notes[i];
    if (item && item.is_task && !item.is_done) {
      count += 1;
    }
  }
  return count;
}

/**
 * Safely parses a note timestamp string or number into epoch milliseconds.
 *
 * @param {string|number|null|undefined} raw Date representation.
 * @return {number} Epoch milliseconds or 0 if invalid.
 */
const parseNoteTimestamp = raw => {
  if (!raw) return 0;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : 0;
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Drawer modal component for managing notes and tasks across Consignments,
 * Purchase Orders, and Transfer Orders.
 *
 * @param {{
 *   isOpen: (boolean|undefined),
 *   orderId: (string|number),
 *   orderType: string,
 *   recordName: (string|undefined),
 *   isNoteAvailable: (boolean|undefined),
 *   getConsignmentByRule: (function(): void|undefined),
 *   selectedCurrentView: (Object|undefined),
 *   setOrderIdForNotes: (function(function(Object): Object): void|undefined),
 *   onOpenTaskCountChange: (function(number): void|undefined)
 * }} props Component props.
 * @return {React.ReactElement} The rendered Notes drawer.
 */
export const NotesModal = ({
  isOpen = true,
  orderId,
  orderType,
  recordName,
  isNoteAvailable,
  getConsignmentByRule,
  selectedCurrentView = {},
  setOrderIdForNotes,
  onOpenTaskCountChange,
}) => {
  const { full_name } = useSelector(state => state.auth.user);
  const [lastUpdatedBy, setLastUpdatedBy] = useState(full_name);
  const [lastActivityTime, setLastActivityTime] = useState(() => format(new Date(), "h:mm a"));
  const [editItem, setEditItem] = useState(null);
  const [saveError, setSaveError] = useState(false);
  const [dueOnly, setDueOnly] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState(() => new Set());
  const pendingDeletes = useRef(new Map());
  const [createNote, { isLoading: creating }] = useCreateConsignmentNoteMutation();
  const [createTaggedTask, { isLoading: creatingTagged }] = useCreateOperationsNoteMutation();
  const [deleteNoteFromServer] = useDeleteNoteMutation();
  const [updateNote, { isLoading: updating }] = useUpdateNoteMutation();
  const dispatch = useDispatch();

  const normalizedOrderType = resolveNoteOrderType(orderType);
  const queryType = selectStateForNotes[normalizedOrderType] || normalizedOrderType;
  const notesQueryString = `related_object_id=${orderId}&type=${queryType}`;

  // The drawer stays mounted while it is closed. Skip the three note queries
  // until it opens; tag invalidation refreshes them after a note is saved.
  const notesReady = isOpen && Boolean(orderId);
  const shipmentNotesQuery = useGetConsignmentNotesQuery(notesQueryString, {
    skip: !notesReady || normalizedOrderType !== "shipment",
    refetchOnMountOrArgChange: true,
  });
  const purchaseNotesQuery = useGetPurchaseOrderNotesQuery(notesQueryString, {
    skip: !notesReady || normalizedOrderType !== "purchase",
    refetchOnMountOrArgChange: true,
  });
  const transferNotesQuery = useGetTransferOrderNotesQuery(notesQueryString, {
    skip: !notesReady || normalizedOrderType !== "transfer",
    refetchOnMountOrArgChange: true,
  });
  const notesQuery = useMemo(() => {
    if (normalizedOrderType === "purchase") return purchaseNotesQuery || {};
    if (normalizedOrderType === "transfer") return transferNotesQuery || {};
    return shipmentNotesQuery || {};
  }, [normalizedOrderType, purchaseNotesQuery, transferNotesQuery, shipmentNotesQuery]);

  const orderSliceName = selectStateForOrder[normalizedOrderType];
  const orderEntityName = selectStateForNotes[normalizedOrderType];
  const storeEntity = useSelector(state =>
    orderSliceName && orderEntityName ? state[orderSliceName]?.[orderEntityName] : null,
  );

  const isDifferentRecord =
    storeEntity?.id != null && orderId != null && String(storeEntity.id) !== String(orderId);
  const storeNotes =
    !isDifferentRecord && Array.isArray(storeEntity?.notes) ? storeEntity.notes : null;

  const notesList = useMemo(() => {
    if (storeNotes) return storeNotes;
    if (Array.isArray(notesQuery?.data)) return notesQuery.data;
    return [];
  }, [storeNotes, notesQuery?.data]);

  useEffect(() => {
    if (!isDifferentRecord && Array.isArray(notesQuery?.data)) {
      dispatchSetNotes(dispatch, normalizedOrderType, notesQuery.data);
    }
  }, [isDifferentRecord, notesQuery?.data, dispatch, normalizedOrderType]);

  const visibleNotes = useMemo(() => {
    const kept = pendingDeleteIds.size
      ? notesList.filter(item => !pendingDeleteIds.has(item?.id))
      : notesList;
    const filtered = dueOnly
      ? kept.filter(item => item && item.is_task && !item.is_done && item.due_date)
      : kept;

    if (filtered.length <= 1) return filtered;

    // Decorate each item with its timestamp and status once to avoid
    // repeated string-parsing or Date allocations inside the comparator.
    const decorated = filtered.map(item => ({
      item,
      isDone: Boolean(item.is_done),
      timestamp: parseNoteTimestamp(item.last_modified || item.date_of_creation),
    }));

    decorated.sort((a, b) => {
      if (a.isDone !== b.isDone) return a.isDone ? 1 : -1;
      return b.timestamp - a.timestamp;
    });

    return decorated.map(entry => entry.item);
  }, [notesList, dueOnly, pendingDeleteIds]);

  const defaultRecordModule =
    orderTypeForUrl[orderType]?.module ||
    orderTypeForUrl[normalizedOrderType]?.module ||
    (normalizedOrderType === "purchase"
      ? "purchase_order"
      : normalizedOrderType === "transfer"
        ? "transfer_order"
        : "consignment");

  const defaultRecord = useMemo(
    () => ({
      id: orderId,
      type: defaultRecordModule,
      name: recordName || String(orderId),
    }),
    [orderId, defaultRecordModule, recordName],
  );

  useEffect(() => {
    onOpenTaskCountChange?.(countOpenTasks(notesList));
  }, [notesList, onOpenTaskCountChange]);

  const invalidateOperations = useCallback(() => {
    dispatch(operationsApiSlice.util.invalidateTags(["OperationsOverview"]));
  }, [dispatch]);

  const recordPayload = useCallback(
    tagged => (tagged?.length ? { records: tagged.map(({ type, id }) => ({ type, id })) } : {}),
    [],
  );

  const saveTask = useCallback(
    async ({ records, body, editingItem }) => {
      if (editingItem) {
        await updateNote({
          id: editingItem.id,
          data: { ...body, ...recordPayload(records) },
          orderId,
          orderType: normalizedOrderType,
        }).unwrap();
        notesQuery.refetch?.();
      } else {
        const includesDefault = (records || []).some(
          record =>
            record.type === defaultRecord.type && String(record.id) === String(defaultRecord.id),
        );
        const create = includesDefault ? createNote : createTaggedTask;
        await create({
          ...body,
          ...recordPayload(records),
          orderType: normalizedOrderType,
          orderId,
          id: orderId,
          type: defaultRecord.type,
        }).unwrap();
        notesQuery.refetch?.();
        if (includesDefault && notesList.length === 0) {
          setOrderIdForNotes?.(state => ({ ...state, is_note_available: true }));
          if (selectedCurrentView.is_view_rule_exists) getConsignmentByRule?.();
        }
      }
      invalidateOperations();
      setLastUpdatedBy(full_name);
      setLastActivityTime(format(new Date(), "h:mm a"));
      setEditItem(null);
    },
    [
      updateNote,
      recordPayload,
      orderId,
      normalizedOrderType,
      notesQuery,
      defaultRecord.type,
      defaultRecord.id,
      createNote,
      createTaggedTask,
      notesList.length,
      setOrderIdForNotes,
      selectedCurrentView.is_view_rule_exists,
      getConsignmentByRule,
      invalidateOperations,
      full_name,
    ],
  );

  // Deletes wait out the Undo window; send them straight away when the drawer
  // closes, the record changes, or the page unloads so a note never comes back.
  useEffect(() => {
    const flushAll = () => pendingDeletes.current.forEach(handle => handle.flush());
    if (!isOpen) flushAll();
    window.addEventListener("beforeunload", flushAll);
    return () => {
      window.removeEventListener("beforeunload", flushAll);
      flushAll();
    };
  }, [isOpen, orderId]);

  const deleteNote = useCallback(
    id => {
      const deleted = notesList.find(item => item.id === id);
      const wasOpenTask = Boolean(deleted?.is_task && !deleted?.is_done);
      const isLastNote = notesList.length === 1;
      let revertModalState;
      if (setOrderIdForNotes) {
        setOrderIdForNotes(previous => {
          revertModalState = previous;
          if (isLastNote) {
            return {
              ...previous,
              is_note_available: false,
              ...(wasOpenTask ? { open_task_count: 0 } : {}),
            };
          }
          if (wasOpenTask) {
            return {
              ...previous,
              open_task_count: Math.max(0, (previous.open_task_count ?? 0) - 1),
            };
          }
          return previous;
        });
      }
      if (editItem?.id === id) setEditItem(null);

      const unhide = () =>
        setPendingDeleteIds(previous => {
          const next = new Set(previous);
          next.delete(id);
          return next;
        });
      setPendingDeleteIds(previous => new Set(previous).add(id));

      const handle = showUndoToast({
        message: deleted?.is_task ? "Task deleted" : "Note deleted",
        onUndo: () => {
          pendingDeletes.current.delete(id);
          unhide();
          if (revertModalState) setOrderIdForNotes?.(revertModalState);
        },
        onCommit: async () => {
          pendingDeletes.current.delete(id);
          try {
            await deleteNoteFromServer({
              id,
              orderId,
              orderType: normalizedOrderType,
              remainingCount: notesList.length,
            }).unwrap();
            setLastActivityTime(format(new Date(), "h:mm a"));
            notesQuery.refetch?.();
          } catch {
            unhide();
            if (revertModalState) setOrderIdForNotes?.(revertModalState);
            setSaveError(true);
          }
        },
      });
      pendingDeletes.current.set(id, handle);
    },
    [
      notesList,
      setOrderIdForNotes,
      editItem?.id,
      deleteNoteFromServer,
      orderId,
      normalizedOrderType,
      notesQuery,
    ],
  );

  const handleCheckboxChange = useCallback(
    async (event, id) => {
      try {
        await updateNote({
          id,
          data: { is_done: event.target.checked },
          orderId,
          orderType: normalizedOrderType,
        }).unwrap();
        invalidateOperations();
        setLastUpdatedBy(full_name);
        setLastActivityTime(format(new Date(), "h:mm a"));
        notesQuery.refetch?.();
      } catch {
        setSaveError(true);
      }
    },
    [updateNote, orderId, normalizedOrderType, invalidateOperations, full_name, notesQuery],
  );

  const toggleEditBox = useCallback(item => {
    setEditItem(current => (current?.id === item.id ? null : item));
  }, []);

  const handleClearEdit = useCallback(() => {
    setEditItem(null);
  }, []);

  if (isNoteAvailable && notesQuery.isLoading && notesList.length === 0) {
    return (
      <Layout>
        <ShimmerUiForNotes />
      </Layout>
    );
  }

  return (
    <Layout>
      <TaskComposer
        defaultRecord={defaultRecord}
        editingItem={editItem}
        onSave={saveTask}
        onClear={handleClearEdit}
        saving={creating || creatingTagged || updating}
      />
      <ScrollRegion>
        <LedgerHeader>
          <LedgerTitle>
            <strong>
              {visibleNotes.length} {visibleNotes.length === 1 ? "task" : "tasks"}
            </strong>
          </LedgerTitle>
          <Filters aria-label="Task filters">
            <button type="button" aria-pressed={!dueOnly} onClick={() => setDueOnly(false)}>
              All Tasks
            </button>
            <button type="button" aria-pressed={dueOnly} onClick={() => setDueOnly(true)}>
              Due Tasks
            </button>
          </Filters>
        </LedgerHeader>
        {visibleNotes.length === 0 && (
          <EmptyState>
            {dueOnly ? "No open tasks with a due date." : "No tasks yet. Add one above."}
          </EmptyState>
        )}
        <NotesList
          listOfItem={visibleNotes}
          handleCheckboxChange={handleCheckboxChange}
          toggleEditBox={toggleEditBox}
          openEditBox={setEditItem}
          deleteNote={deleteNote}
          compactDueDate
        />
        {saveError && <p role="alert">Could not update the task. Please try again.</p>}
        {notesList.length > 0 && (
          <ActivityFooter>
            Last activity recorded by {lastUpdatedBy} at {lastActivityTime}
          </ActivityFooter>
        )}
      </ScrollRegion>
    </Layout>
  );
};
